import * as Sentry from "@sentry/react";
import {
  getAccessToken,
  initLogOut
} from "openstack-uicore-foundation/lib/security/methods";
import {
  currencyAmountFromCents,
  formatDiscount
} from "openstack-uicore-foundation/lib/utils/money";
import moment from "moment-timezone";
import {
  DECIMALS,
  DEFAULT_DEADLINE_TOLERANCE_SECONDS,
  FILE_UPLOAD_STATUS,
  MILISECONDS,
  SECONDS_PER_MINUTE,
  UPLOAD_PROFILES
} from "./constants";

const isSentryInitialized = () => !!window.SENTRY_DSN;

export const getAccessTokenSafely = async () => {
  try {
    return await getAccessToken();
  } catch (e) {
    Sentry.captureException(e);
    return initLogOut();
  }
};

export const handleSentryException = (err) =>
  isSentryInitialized()
    ? Sentry.captureException(err)
    : console.log("Error on registration: ", err);

export const formatDate = (
  date,
  timeZone = "LOC",
  format = "dddd Do h:mm a"
) => {
  if (timeZone === "LOC") {
    return moment(date * MILISECONDS).format(format);
  }

  return moment(date * MILISECONDS)
    .tz(timeZone)
    .format(format);
};

// converts bytes to KB, MB or GB depending on the size
export const formatBytes = (bytes, decimals = DECIMALS) => {
  if (!+bytes) return "0 KB";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  let i = Math.floor(Math.log(bytes) / Math.log(k));
  i = Math.max(i, 1);

  const value = bytes / k ** i;

  return `${parseFloat(value.toFixed(dm))} ${sizes[i]}`;
};

export const getUploadStatus = (module) => {
  const DEADLINE_TOLERANCE = parseInt(
    process.env.UPLOAD_DEADLINE_TOLERANCE || DEFAULT_DEADLINE_TOLERANCE_SECONDS
  );

  let status = FILE_UPLOAD_STATUS.COMPLETE;
  if (!module.media_upload) {
    if (module.upload_deadline < moment().unix()) {
      status = FILE_UPLOAD_STATUS.DEADLINE_MISSED;
    } else if (
      module.upload_deadline <
      moment().add(DEADLINE_TOLERANCE, "seconds").unix()
    ) {
      status = FILE_UPLOAD_STATUS.DEADLINE_ALERT;
    } else {
      status = FILE_UPLOAD_STATUS.PENDING;
    }
  }

  return status;
};

export const hasPermission = (groups, permissions) =>
  groups.some(({ slug }) => permissions.includes(slug));

export const normalizeOrder = (data) => ({
  ...data,
  total: data.net_amount || data.amount_due || 0,
  forms: data.forms.map((form) => ({
    ...form,
    addon_name: form.addon_name || form.add_on?.name || "",
    discount: formatDiscount(form.discount_amount, form.discount_type),
    discount_total: form.discount_in_cents || 0,
    amount: currencyAmountFromCents(form.net_amount || 0)
  }))
});

const MMSS_PAD_WIDTH = 2;

export const formatMmSs = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${String(minutes).padStart(MMSS_PAD_WIDTH, "0")}:${String(seconds).padStart(MMSS_PAD_WIDTH, "0")}`;
};

export const htmlToString = (html) =>
  new DOMParser().parseFromString(html, "text/html").documentElement
    .textContent || "";

export const pickUploadProfile = () => {
  const { connection } = navigator;

  if (!connection) {
    return UPLOAD_PROFILES.SLOW;
  }

  if (connection.saveData) {
    return UPLOAD_PROFILES.SLOW;
  }

  if (connection.effectiveType === "4g") {
    return UPLOAD_PROFILES.FAST;
  }

  return UPLOAD_PROFILES.SLOW;
};
